/** Responsive recent-project navigation sourced from the projects API. */
import {
	Alert,
	Box,
	Button,
	Chip,
	Divider,
	Skeleton,
	Stack,
	Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import RefreshIcon from "@mui/icons-material/Refresh";
import type { ProjectSummaryDTO } from "@flaha/shared-types";
import { Link as RouterLink } from "react-router-dom";

import { GlassSurface } from "../../../layouts/components/GlassSurface";

export interface RecentProjectsPanelProps {
	projects: ProjectSummaryDTO[] | null;
	error: string | null;
	limit: number;
	onRetry: () => void;
}

export function RecentProjectsPanel({
	projects,
	error,
	limit,
	onRetry,
}: RecentProjectsPanelProps) {
	const recent = (projects ?? []).slice(0, limit);

	return (
		<GlassSurface
			component="section"
			aria-labelledby="recent-projects-title"
			intensity="standard"
			depth="raised"
			sx={{ minWidth: 0, height: "100%" }}
		>
			<Stack
				direction={{ xs: "column", sm: "row" }}
				alignItems={{ xs: "flex-start", sm: "center" }}
				justifyContent="space-between"
				gap={1.5}
				sx={{ p: 2.5 }}
			>
				<Box>
					<Typography id="recent-projects-title" component="h2" variant="h6">
						Recent projects
					</Typography>
					<Typography variant="caption" sx={{ color: "workspace.textMuted" }}>
						Latest rows supplied by the active organization’s project request
					</Typography>
				</Box>
				<Button
					component={RouterLink}
					to="/projects"
					variant="outlined"
					size="small"
					sx={{ color: "workspace.textPrimary", borderColor: "workspace.borderStrong" }}
				>
					Manage projects
				</Button>
			</Stack>
			<Divider sx={{ borderColor: "workspace.border" }} />
			<Box sx={{ p: 2.5 }} aria-busy={projects === null && !error}>
				{error && (
					<Alert
						severity="error"
						action={
							<Button color="inherit" size="small" startIcon={<RefreshIcon />} onClick={onRetry}>
								Retry
							</Button>
						}
					>
						Projects are unavailable: {error}
					</Alert>
				)}
				{projects === null && !error && (
					<Stack spacing={1.25} aria-label="Loading recent projects">
						{[0, 1, 2].map((row) => (
							<Skeleton
								key={row}
								variant="rounded"
								height={68}
								sx={{ bgcolor: "rgba(246,240,227,0.09)" }}
							/>
						))}
					</Stack>
				)}
				{projects !== null && projects.length === 0 && (
					<Stack
						alignItems="center"
						spacing={1.5}
						textAlign="center"
						sx={{ py: 4 }}
					>
						<Typography variant="h6">No projects in this organization</Typography>
						<Typography variant="body2" sx={{ color: "workspace.textSecondary", maxWidth: 480 }}>
							Create a project to establish the agronomic context for samples and soil tests.
						</Typography>
						<Button component={RouterLink} to="/projects" variant="contained" startIcon={<AddIcon />}>
							Create project
						</Button>
					</Stack>
				)}
				{recent.length > 0 && (
					<Stack spacing={1.25}>
						{recent.map((project) => (
							<Box
								key={project.id}
								component={RouterLink}
								to={`/projects/${project.id}`}
								sx={(theme) => ({
									display: "block",
									minWidth: 0,
									p: 1.75,
									borderRadius: 1.5,
									border: `1px solid ${theme.palette.workspace.border}`,
									color: "inherit",
									textDecoration: "none",
									transition: theme.motion.transition.surface,
									"&:hover, &:focus-visible": {
										borderColor: "workspace.blue",
										backgroundColor: "rgba(74,156,197,0.07)",
									},
								})}
							>
								<Stack direction="row" alignItems="center" gap={1.25} flexWrap="wrap">
									<Box sx={{ minWidth: 0, flex: "1 1 220px" }}>
										<Typography variant="subtitle1" noWrap>
											{project.name}
											{project.code ? ` · ${project.code}` : ""}
										</Typography>
										<Typography component="time" dateTime={project.updatedAt} variant="caption" sx={{ color: "workspace.textMuted" }}>
											Updated {new Date(project.updatedAt).toLocaleString()}
										</Typography>
									</Box>
									<Chip label={`${project.sampleCount} sample${project.sampleCount === 1 ? "" : "s"}`} size="small" />
									<Chip
										label={project.status === "ACTIVE" ? "Active" : "Archived"}
										size="small"
										color={project.status === "ACTIVE" ? "success" : "default"}
										variant="outlined"
									/>
									<ArrowForwardIcon aria-hidden="true" fontSize="small" sx={{ color: "workspace.textMuted" }} />
								</Stack>
							</Box>
						))}
					</Stack>
				)}
			</Box>
		</GlassSurface>
	);
}
